import React, { useMemo, useRef, useState } from 'react';
import { Button, MenuItem, SelectField, Stack } from '@curves/ui-base';

import CyclePurchaseDialogBox from '@Local/CyclePurchase/Components/CyclePurchaseDialogBox';
import { PageLayout } from '@Local/Components/Layout';
import { Grid } from '@mui/material';
import { OverviewListLayout } from '@Local/Components/OverviewList';
import useCyclePurchaseApi from '@Apis/useCyclePurchaseApi';
import useOptions from '@Util/hook/useOptions';
import useOverview from '@Util/hook/useOverview';
import { refIsRequiredError } from '@Util/utils';
import { getCyclePurchaseVerificationBatchPaymentRowLabel } from '@Local/CyclePurchase/Components/CyclePurchaseVerificationBatchPaymentTemplate';
import { isReturnTaskStatus } from '@Local/CyclePurchase/staticData';
const initBookmark = () => ({
    pageIndex: 1,
    pageSize: 10,
});
// 扣款／退單分頁定義
const TASK_TABS = [
    { label: '扣款', value: 'billing' },
    { label: '退單', value: 'return' },
];
// 狀態「全部」。後端 OptionHelper 的 value 一律為字串，此處與其一致以利下拉比對
const ALL_STATUS_VALUE = '99';
const CyclePurchaseVerificationBatchPaymentPage = (props) => {
    const { getCyclePurchaseTasksOverviewApi, getCyclePurchaseTasksOverviewOptionsApi } = useCyclePurchaseApi();
    const { options } = useOptions({
        getOptionsApi: getCyclePurchaseTasksOverviewOptionsApi,
        isFetchOnMount: true,
    });
    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: getCyclePurchaseTasksOverviewApi,
        defaultSearchParams: {
            status: 99,
            ...initBookmark(),
        },
        isFetchOnMount: true,
    });
    const statusRef = useRef(null);
    const refreshRef = useRef(getOverviewData);
    const dialogRef = useRef(null);
    const [activeTab, setActiveTab] = useState('billing');
    // 狀態下拉改為受控：選項為非同步載入，非受控的 defaultValue 會因掛載時選項尚未產生而顯示空白
    // （切到退單分頁時特別明顯）。值型別沿用後端回傳的字串，與原本 getResult() 取得的值一致。
    const [status, setStatus] = useState(ALL_STATUS_VALUE);
    const { openUploadCyclePurchaseOrderSheetDialog, openUploadCyclePurchaseReturnOrderSheetDialog } = dialogRef.current || {};
    const handleSubmit = (e) => {
        const isError = refIsRequiredError(statusRef);
        if (!isError) {
            const params = {
                ...initBookmark(),
                status: status,
            };
            getOverviewData(params);
        }
    };
    const handlePageChange = (e, page) => {
        updateOverviewPage(page);
    };
    // 切換扣款／退單分頁：狀態重置為「全部」並重新查詢
    const handleTabChange = (_, value) => {
        setActiveTab(value);
        setStatus(ALL_STATUS_VALUE);
        getOverviewData({ ...initBookmark(), status: ALL_STATUS_VALUE });
    };
    // 依分頁篩選狀態下拉選項（全部選項兩分頁皆顯示）
    // 注意：後端 OptionHelper 回傳的 value 為字串，需轉數字比對，否則「全部」會在退單分頁被濾掉
    const statusOptions = (options?.taskStatusOption || []).filter(
        (item) => Number(item.value) === Number(ALL_STATUS_VALUE) || isReturnTaskStatus(item.value) === (activeTab === 'return'),
    );
    // 依分頁篩選列表資料，確保退單分頁僅顯示狀態11~19的任務
    const filteredList = (overviewData?.list || []).filter((row) => isReturnTaskStatus(row?.taskStatus) === (activeTab === 'return'));
    const tabActions =
        activeTab === 'return'
            ? [{ label: '上傳退單總表', variant: 'outlined', onClick: openUploadCyclePurchaseReturnOrderSheetDialog }]
            : [{ label: '匯入主檔', variant: 'outlined', onClick: openUploadCyclePurchaseOrderSheetDialog }];
    const headerRow = useMemo(
        () => {
            return getCyclePurchaseVerificationBatchPaymentRowLabel();
        },
        // eslint-disable-next-line
        [],
    );
    return (
        <React.Fragment>
            <PageLayout>
                <PageLayout.Tabs tabs={TASK_TABS} activeTab={activeTab} onTabChange={handleTabChange} actions={tabActions} />
                <OverviewListLayout
                    contextValue={{
                        dialogRef,
                        refresh: refreshRef.current,
                        setRefresh: (fn) => (refreshRef.current = fn),
                    }}
                >
                    <OverviewListLayout.SearchBar onSubmit={handleSubmit}>
                        <Grid spacing={2} container>
                            <Grid xs={8} item>
                                <SelectField
                                    ref={statusRef}
                                    label={'狀態'}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    direction="row"
                                    displayEmpty
                                    fullWidth
                                >
                                    {statusOptions.map((item) => {
                                        return (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.text}
                                            </MenuItem>
                                        );
                                    })}
                                </SelectField>
                            </Grid>
                            <Grid xs={4} item>
                                <Stack className={'h-100'} alignItems={'center'}>
                                    <Button type={'submit'} variant={'contained'} color="primary">
                                        搜尋
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </OverviewListLayout.SearchBar>
                    <OverviewListLayout.Table
                        headerRow={headerRow}
                        list={filteredList}
                        tableRowBodyProps={{ dialogRef: dialogRef }}
                        totalPage={overviewData?.pagination?.totalPage}
                        pageIndex={overviewData?.pagination?.pageIndex}
                        refresh={() => getOverviewData(searchParams)}
                        onChangePage={handlePageChange}
                        isPagination
                    />
                </OverviewListLayout>
            </PageLayout>
            <CyclePurchaseDialogBox ref={dialogRef} />
        </React.Fragment>
    );
};
export default CyclePurchaseVerificationBatchPaymentPage;
